// src/components/Admin/EvaluationStudents/EvaluationStudents.js
import React, { useState, useEffect, useCallback } from "react";
import { Loader, Message, Icon } from "semantic-ui-react";
import { StudentAPI, DidacticRoutesAPI } from "../../../../api";
import { useTranslation } from "react-i18next";
import "./EvaluationStudents.scss";

const studentAPI = new StudentAPI();
const didacticRoutesAPI = new DidacticRoutesAPI();

export function EvaluationStudents({
  teacherInstitutionId,
  classNumber,
  accessToken,
  route,
  activeQuizId,
  onRankingClick,
}) {
  const { t } = useTranslation("evaluations");
  const [students, setStudents] = useState([]);
  const [assistantsData, setAssistantsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  const fetchStudents = useCallback(
    async (currentPage) => {
      try {
        if (!teacherInstitutionId || !classNumber) return;
        setLoading(true);
        setError(null);

        const result = await studentAPI.getStudentsByClassnumber(
          accessToken,
          teacherInstitutionId,
          classNumber,
        );
        setStudents(result.students || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, teacherInstitutionId, classNumber],
  );

  const fetchRouteAssistants = useCallback(async () => {
    try {
      if (!route) return;
      setLoading(true);

      const routeRes = await didacticRoutesAPI.getRouteById(accessToken, route);
      const routeData = routeRes.route;
      if (!routeData) return;

      if (Array.isArray(routeData.assistants)) {
        const allAssistants = routeData.assistants.map(
          (item) => item.assistant,
        );
        setAssistantsData(allAssistants);
      } else {
        setAssistantsData([]);
      }
    } catch (error) {
      console.error("Error al cargar la ruta o los asistentes:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, route]);

  useEffect(() => {
    fetchStudents(page);
  }, [fetchStudents, page]);
  useEffect(() => {
    setPage(1);
  }, [classNumber]);
  useEffect(() => {
    fetchRouteAssistants();
  }, [fetchRouteAssistants]);

  if (loading) {
    return (
      <Loader active inline="centered">
        {t("rutasPanel.loading")}
      </Loader>
    );
  }

  if (error) {
    return (
      <Message negative>
        <Message.Header>{t("studentsPanel.errorTitle")}</Message.Header>
        <p>{error}</p>
      </Message>
    );
  }

  if (!classNumber) {
    return (
      <p>
        <div className="intro-sections">
          <div className="intro-section rutas-creadas">
            <h2 className="intro-title">{t("studentsPanel.listTitle")}</h2>
            <p className="intro-description">
              {t("studentsPanel.listDescription")}
            </p>
            <hr className="intro-divider" />
          </div>
        </div>
      </p>
    );
  }

  return (
    <div className="evaluation-students-panel">
      <div className="students-grid">
        {/* Tarjeta RankingQuiz */}
        {activeQuizId && (
          <div
            className="e-card-student-compact ranking-quiz-card"
            onClick={() => onRankingClick(activeQuizId)}
          >
            <div className="student-avatar">
              <Icon name="chart bar" size="large" />
            </div>
            <div className="student-info">
              <h3 className="student-name">RankingQuiz</h3>
            </div>
          </div>
        )}

        {/* Asistentes */}
        <p>
          <div className="intro-sections">
            <div className="intro-section rutas-creadas">
              <h2 className="intro-title">{t("studentsPanel.assignedAI")}</h2>
              <hr className="intro-divider" />
            </div>
          </div>
        </p>
        {assistantsData.map((asst) => (
          <div key={asst._id || asst.title} className="e-card-student-compact">
            <div className="student-avatar">
              <img
                src={asst.miniature || "https://via.placeholder.com/80"}
                alt="Avatar asistente"
              />
            </div>
            <div className="student-info">
              <h3 className="student-name">{asst.title}</h3>
              <p className="student-desc">
                {t("studentsPanel.virtualAssistant")}
              </p>
            </div>
            <div className="student-status-dot circle--blue" />
          </div>
        ))}

        {/* Estudiantes */}
        <p>
          <div className="intro-sections">
            <div className="intro-section rutas-creadas">
              <h2 className="intro-title">
                {t("studentsPanel.studentsInItinerary")}
              </h2>
              <hr className="intro-divider" />
            </div>
          </div>
        </p>
        {students.length === 0 ? (
          <Message info>
            <Message.Header>{t("studentsPanel.noStudents")}</Message.Header>
            <p>{t("studentsPanel.noStudentsContent")}</p>
          </Message>
        ) : (
          students.map((std) => (
            <div key={std._id} className="e-card-student-compact">
              <div className="student-avatar">
                <img
                  src={std.avatar || "https://via.placeholder.com/80"}
                  alt="Avatar estudiante"
                />
              </div>
              <div className="student-info">
                <h3 className="student-name">
                  {std.name} {std.surname}
                </h3>
                <p className="student-desc">{std.classnumber}</p>
              </div>
              <div className="student-status-dot circle--blue" />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
